import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useRevit } from '../../contexts/RevitContext';
import './Links.css';
import { useAuth } from '@clerk/clerk-react';
import { handleLoginWithYouTube, logoutFromYt } from '../../utils/apiCalls';
import Icon from '../Icon';
import TagInput from './TagInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { generateRevtrackLink, extractVideoId } from '../../utils/linkUtils';
import { AddVideoTracking } from './AddVideoTracking';
import QrCodeGenerator from './QrCodeGenerator';

const TABS = [
    { id: 'generator', label: 'Link Generator' },
    { id: 'auto',      label: 'Automatic Link Updating' },
    { id: 'qr',        label: 'QR Code Generator' },
    { id: 'tools',     label: 'Other Tools' },
];

const Links = () => {
    const [activeTab, setActiveTab] = useState('generator');
    const [resetVideoLink, setResetVideoLink] = useState('');
    const [oldLink, setOldLink] = useState('');
    const [newLink, setNewLink] = useState('');
    const [isReplacing, setIsReplacing] = useState(false);
    const [targetUrl, setTargetUrl] = useState('');
    const [targetUrlForAll, setTargetUrlForAll] = useState('');
    const [linkType, setLinkType] = useState('youtube');
    const [landingPage, setLandingPage] = useState('');
    const [youtubeLink, setYoutubeLink] = useState('');
    const [paramValue, setParamValue] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [landingPages, setLandingPages] = useState([]);
    const [copySuccess, setCopySuccess] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [customUrl, setCustomUrl] = useState('');
    const [customTags, setCustomTags] = useState('');
    const [publishedDate, setPublishedDate] = useState('');
    const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
    const [showNewLandingPageInput, setShowNewLandingPageInput] = useState(false);
    const [newLandingPageValue, setNewLandingPageValue] = useState('');
    const [showTrackingExplanation, setShowTrackingExplanation] = useState(false);

    const dropdownRef = useRef(null);

    const { username, youtubeName, youtubeThumbUrl, setYoutubeName } = useRevit();
    const { getToken } = useAuth();

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const VPS_URL = process.env.REACT_APP_VPS_URL;
    console.log(VPS_URL)

    const createLink = async (utm, url = null, tags = null, type, published_at = null) => {
        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/create-resource/${username}`,
                {
                    type: type,
                    name: utm,
                    url: url,
                    tags: tags,
                    published_at: published_at
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            console.log('Resource created successfully:', response.data);
        } catch (error) {
            console.error('Error creating link:', error);
            alert('Error creating link');
        }
    };

    const handleLogout = async () => {
        const token = await getToken();
        try {
            const result = await logoutFromYt(username, token);
            if (result) {
                setYoutubeName(null);
            }
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    useEffect(() => {
        // Load saved landing pages from local storage
        const savedLandingPages = JSON.parse(localStorage.getItem('landingPages')) || [];
        setLandingPages(savedLandingPages);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                if (showNewLandingPageInput && newLandingPageValue.trim()) {
                    handleAddNewLandingPage();
                } else {
                    setShowDropdown(false);
                    setShowNewLandingPageInput(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNewLandingPageInput, newLandingPageValue]);

    const saveLandingPage = (newPage) => {
        const savedLandingPages = JSON.parse(localStorage.getItem('landingPages')) || [];
        if (!savedLandingPages.includes(newPage)) {
            savedLandingPages.push(newPage);
            localStorage.setItem('landingPages', JSON.stringify(savedLandingPages));
            setLandingPages(savedLandingPages);
        }
    };

    const handleAddNewLandingPage = () => {
        if (newLandingPageValue.trim()) {
            saveLandingPage(newLandingPageValue.trim());
            setLandingPage(newLandingPageValue.trim());
            setNewLandingPageValue('');
            setShowNewLandingPageInput(false);
            setShowDropdown(false);
        }
    };

    const handleNewLandingPageKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddNewLandingPage();
        }
    };



    const replaceLinksInVideos = async () => {
        setIsReplacing(true);
        const token = await getToken();
        try {
            const response = await axios.put(`https://${SERVER_URL}/api/replace-link-in-videos`, 
                {
                    userId: username,
                    oldLink,
                    newLink
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            alert(response.data.message);
        } catch (error) {
            console.error('Error replacing links:', error);
            alert('Error replacing links');
        } finally {
            setIsReplacing(false);
        }
    };

    const cleanLinkInVideo = async () => {
        const videoId = extractVideoId(resetVideoLink);
        if (!videoId) {
            alert('Invalid YouTube video link');
            return;
        }

        const token = await getToken();
        try {
            const response = await axios.put(`https://${SERVER_URL}/api/clean-link-in-video/${videoId}`,
                {
                    userId: username,
                    targetUrl
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            alert(response.data.message);
        } catch (error) {
            console.error('Error cleaning link in video:', error);
            alert('Error cleaning link in video');
        }
    };

    const cleanLinkInAllVideos = async () => {
        setIsReplacing(true);
        const token = await getToken();
        try {
            const response = await axios.put(`https://${SERVER_URL}/api/clean-link-in-all-videos`, 
                {
                    userId: username,
                    targetUrl: targetUrlForAll
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            alert(response.data.message);
        } catch (error) {
            console.error('Error cleaning links in all videos:', error);
            alert('Error cleaning links in all videos');
        } finally {
            setIsReplacing(false);
        }
    };

    const handleUTMFormSubmit = async (e) => {
        e.preventDefault();

        const selectedLandingPage = landingPage || (document.getElementById('landingPageDropdown') || {}).value;

        if (!selectedLandingPage) {
            setGeneratedLink('Please enter a valid landing page!');
            return;
        }

        if (landingPage) {
            saveLandingPage(landingPage);
        }

        let utmLink;
        if (linkType === 'youtube' || linkType === 'pinComment') {
            const videoID = extractVideoId(youtubeLink);
            if (!videoID) {
                setGeneratedLink('Please enter a valid YouTube video URL!');
                return;
            }

            utmLink = generateRevtrackLink(selectedLandingPage, linkType, videoID);
        } else {
            if (!paramValue) {
                setGeneratedLink('Please enter a value for the parameter!');
                return;
            }

            utmLink = generateRevtrackLink(selectedLandingPage, linkType, paramValue);
        }

        // Create link in database if custom URL, tags, or published date are provided (for any link type)
        if (customUrl || customTags || publishedDate) {
            const tags = customTags ? customTags.split(',').map(tag => tag.trim()).filter(tag => tag) : null;
            const utmParam = (linkType === 'youtube' || linkType === 'pinComment') ? extractVideoId(youtubeLink) : paramValue;
            const publishedAtValue = publishedDate ? new Date(publishedDate).toISOString() : null;
            await createLink(utmParam, customUrl, tags, linkType, publishedAtValue);
        }

        setGeneratedLink(utmLink);
        setCopySuccess(''); // Reset copy success message
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink).then(() => {
            setCopySuccess('Link copied to clipboard!');
        }).catch(err => {
            console.error('Could not copy link: ', err);
        });
    };

    const linkTypeLabelMap = {
        youtube:     'description of the video',
        pinComment:  'pinned comment of the video',
        email:       'email',
        community:   'community post',
        channel:     'channel description',
        instagram:   'Instagram post',
        twitter:     'Twitter post',
        threads:     'Threads post',
        linkedin:    'LinkedIn post',
        podcast:     'podcast',
        blog:        'blog post',
        website:     'website',
        tiktok:      'TikTok video',
        ad:          'ad',
        telegram:    'Telegram post',
        facebook:    'Facebook post',
        discord:     'Discord post',
        live:        'live stream',
        webinar:     'webinar',
        leadMagnet:  'lead magnet',
    };

    const getLinkPlacementHint = () => {
        if (linkType === 'youtube' || linkType === 'pinComment') {
            const label = linkTypeLabelMap[linkType];
            return `Now place this link in the ${label}${youtubeLink ? ': ' + youtubeLink : ''}`;
        }
        const label = linkTypeLabelMap[linkType] || linkType;
        return `Now place this link in the ${label}${paramValue ? ': ' + paramValue : ''}`;
    };

    const removeLandingPage = (pageToRemove) => {
        const updatedPages = landingPages.filter(page => page !== pageToRemove);
        setLandingPages(updatedPages);
        localStorage.setItem('landingPages', JSON.stringify(updatedPages));
    };

    return (
        <div className="links-container">

            {/* ── YouTube account bar (always visible) ── */}
            <div className="top-actions-container">
                <div className="youtube-account-container">
                    {youtubeName ? (
                        <div className="youtube-account-info">
                            <p>Logged in as:</p>
                            <div className="youtube-account-details">
                                {youtubeThumbUrl && (
                                    <img
                                        src={youtubeThumbUrl}
                                        alt="Channel"
                                        className="channel-thumbnail"
                                        style={{ width: '28px', height: '28px', borderRadius: '50%', marginRight: '8px', verticalAlign: 'middle' }}
                                    />
                                )}
                                {youtubeName}
                                <Icon
                                    name="logout"
                                    className="yt-logout-icon"
                                    onClick={handleLogout}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="youtube-account-info">
                            <button className="youtube-login-button" onClick={() => handleLoginWithYouTube(username)}>
                                <Icon name="youtube" size={20} />
                                Login with YouTube
                            </button>
                        </div>
                    )}
                </div>

            </div>

            {/* ── Intro explanation ── */}
            <div className="links-intro">
                <div className="links-intro-header">
                    <h2 className="links-intro-title">Tracking Links</h2>
                    <button
                        className="extension-download-button-top"
                        onClick={() => window.open('https://chromewebstore.google.com/detail/revtrack-link-generator/dpckjhaijnlbkldnbgkdjclehkhopmap?authuser=0&hl=en-GB&pli=1', '_blank')}
                        title="Download RevTrack Chrome Extension"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        Chrome Extension
                    </button>
                </div>
                <p className="links-intro-text">
                    Use the <strong>Link Generator</strong> to create a tracking link for any placement and add it manually,
                    or connect your YouTube channel and let <strong>Automatic Link Updating</strong> add tracking links to all your videos in one click.
                </p>
                <div className="tracking-explanation-toggle">
                    <button
                        className="tracking-explanation-button"
                        onClick={() => setShowTrackingExplanation(!showTrackingExplanation)}
                    >
                        How tracking links work
                        <span className={`te-chevron${showTrackingExplanation ? ' te-chevron--open' : ''}`}>▼</span>
                    </button>
                </div>
                {showTrackingExplanation && (
                    <div className="tracking-explanation-box">
                        <h4>How Tracking Links Work</h4>
                        <p>
                            RevTrack adds special parameters to your URLs that identify the traffic source when someone clicks and converts.
                            This allows you to track which content pieces (videos, emails, posts, etc.) are driving your sales.
                        </p>
                        
                        <h4>Anatomy of a Tracking Link</h4>
                        <div className="tracking-link-anatomy">
                            <div className="anatomy-example">
                                <code>
                                    <span className="anatomy-base">example.com</span>
                                    <span className="anatomy-separator">?</span>
                                    <span className="anatomy-param">video</span>
                                    <span className="anatomy-equals">=</span>
                                    <span className="anatomy-value">dQw4w9WgXcQ</span>
                                </code>
                            </div>
                            
                            <div className="anatomy-explanations">
                                <div className="anatomy-item">
                                    <span className="anatomy-label anatomy-base">example.com</span>
                                    <span className="anatomy-description">Your landing page URL</span>
                                </div>
                                <div className="anatomy-item">
                                    <span className="anatomy-label anatomy-separator">?</span>
                                    <span className="anatomy-description">Separator that starts URL parameters</span>
                                </div>
                                <div className="anatomy-item">
                                    <span className="anatomy-label anatomy-param">video</span>
                                    <span className="anatomy-description">Parameter name (the type of content: video, email, community, etc.)</span>
                                </div>
                                <div className="anatomy-item">
                                    <span className="anatomy-label anatomy-equals">=</span>
                                    <span className="anatomy-description">Connects parameter name to its value</span>
                                </div>
                                <div className="anatomy-item">
                                    <span className="anatomy-label anatomy-value">dQw4w9WgXcQ</span>
                                    <span className="anatomy-description">Parameter value (For YouTube videos, this is the video ID. For other content, this is a custom name you give it, eg email1.)</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="tracking-tip">
                            <strong>💡 Tip:</strong> When you upload a new YouTube video, you can create this link manually by adding 
                            <code>?video=VIDEO_ID</code> to the end of your landing page URL, replacing VIDEO_ID with the actual video ID 
                            from the YouTube URL. The ID of the video is the last part of the URL, ie for https://www.youtube.com/watch?v=OEoeCaNKYXY to ID is OEoeCaNKYXY.
                        </div>
                    </div>
                )}
            </div>

            {/* ── Tab navigation ── */}
            <div className="links-tab-nav">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`links-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Tab content ── */}
            <div className="links-section">

                {/* ── Link Generator ── */}
                {activeTab === 'generator' && (
                    <div className="utm-link-generator">
                        <h3 className="section-headline">RevTrack Link Generator</h3>
                        <form onSubmit={handleUTMFormSubmit}>
                            <label htmlFor="linkType">Where will this link go?</label>
                            <select id="linkType" value={linkType} onChange={(e) => setLinkType(e.target.value)}>
                                <option value="youtube">YouTube Video</option>
                                <option value="pinComment">Pinned Comment</option>
                                <option value="email">Email</option>
                                <option value="channel">Channel</option>
                                <option value="community">Community</option>
                                <option value="ad">Ad</option>
                                <option value="blog">Blog</option>
                                <option value="discord">Discord</option>
                                <option value="facebook">Facebook</option>
                                <option value="instagram">Instagram</option>
                                <option value="leadMagnet">Lead Magnet</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="live">Live Stream</option>
                                <option value="podcast">Podcast</option>
                                <option value="telegram">Telegram</option>
                                <option value="threads">Threads</option>
                                <option value="tiktok">TikTok</option>
                                <option value="twitter">Twitter</option>
                                <option value="webinar">Webinar</option>
                                <option value="website">Website</option>
                            </select>

                            {landingPages.length > 0 && (
                                <>
                                    <label htmlFor="landingPageDropdown">Choose Landing Page:</label>
                                    <div className="custom-dropdown" ref={dropdownRef}>
                                        <div
                                            className="dropdown-header"
                                            onClick={() => setShowDropdown(!showDropdown)}
                                        >
                                            {landingPage || "Select a landing page"}
                                        </div>
                                        {showDropdown && (
                                            <div className="dropdown-list">
                                                {landingPages.map((page, index) => (
                                                    <div key={index} className="dropdown-item">
                                                        <span
                                                            className="dropdown-item-text"
                                                            onClick={() => {
                                                                setLandingPage(page);
                                                                setShowDropdown(false);
                                                                setShowNewLandingPageInput(false);
                                                            }}
                                                            style={{
                                                                wordBreak: "break-word",
                                                                whiteSpace: "normal",
                                                                overflowWrap: "break-word",
                                                                display: "block",
                                                                width: "100%",
                                                                padding: "8px 0"
                                                            }}
                                                        >
                                                            {page}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="remove-landing-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeLandingPage(page);
                                                            }}
                                                            style={{ flexShrink: 0, marginLeft: "8px" }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                                {!showNewLandingPageInput ? (
                                                    <div className="dropdown-item add-new-option">
                                                        <span
                                                            className="dropdown-item-text"
                                                            onClick={() => setShowNewLandingPageInput(true)}
                                                            style={{
                                                                display: "block",
                                                                width: "100%",
                                                                padding: "8px 0",
                                                                color: "var(--Primary-500)",
                                                                fontWeight: "600"
                                                            }}
                                                        >
                                                            + Add new
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="dropdown-item new-landing-input">
                                                        <input
                                                            type="url"
                                                            className="new-landing-page-input"
                                                            value={newLandingPageValue}
                                                            onChange={(e) => setNewLandingPageValue(e.target.value)}
                                                            onKeyDown={handleNewLandingPageKeyDown}
                                                            placeholder="https://example.com"
                                                            autoFocus
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {landingPages.length === 0 && (
                                <>
                                    <label htmlFor="landingPage">Enter Landing Page URL:</label>
                                    <input
                                        type="url"
                                        id="landingPage"
                                        value={landingPage}
                                        onChange={(e) => setLandingPage(e.target.value)}
                                        placeholder="https://example.com"
                                    />
                                </>
                            )}

                            {(linkType === 'youtube' || linkType === 'pinComment') && (
                                <div id="youtubeSection">
                                    <div className="link-form-field">
                                        <label htmlFor="youtubeLink">Enter YouTube Video URL:</label>
                                        <input
                                            type="url"
                                            id="youtubeLink"
                                            value={youtubeLink}
                                            onChange={(e) => setYoutubeLink(e.target.value)}
                                            placeholder="https://www.youtube.com/watch?v=example"
                                        />
                                    </div>
                                    <div className="additional-details-toggle" onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}>
                                        <span>Additional Details (optional)</span>
                                        <span className={`toggle-icon ${showAdditionalDetails ? 'open' : ''}`}>▼</span>
                                    </div>
                                    {showAdditionalDetails && (
                                        <>
                                            <div className="link-form-field">
                                                <label htmlFor="customUrl">
                                                    Enter post/page URL (optional):
                                                    <div className="tooltip-icon">
                                                        ?
                                                        <div className="tooltip-text">
                                                            Enter the URL of the source you want to track clicks for. This could be the Instagram/Linkedin/X etc post where this link will go. This will be stored and linked to your tracking data.
                                                        </div>
                                                    </div>
                                                </label>
                                                <input
                                                    type="url"
                                                    id="customUrl"
                                                    value={customUrl}
                                                    onChange={(e) => setCustomUrl(e.target.value)}
                                                    placeholder="https://example.com/video"
                                                />
                                            </div>
                                            <div className="link-form-field">
                                                <label htmlFor="customTags">Custom Tags (optional):</label>
                                                <TagInput
                                                    value={customTags}
                                                    onChange={setCustomTags}
                                                    placeholder="tag1, tag2, tag3"
                                                />
                                            </div>
                                            <div className="link-form-field">
                                                <label htmlFor="publishedDate">Published Date (optional):</label>
                                                <input
                                                    type="date"
                                                    id="publishedDate"
                                                    value={publishedDate}
                                                    onChange={(e) => setPublishedDate(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {linkType !== 'youtube' && linkType !== 'pinComment' && (
                                <div id="customSection">
                                    <div className="link-form-field">
                                        <label htmlFor="paramValue">Enter a name (e.g., video name, email subject, etc.):</label>
                                        <input
                                            type="text"
                                            id="paramValue"
                                            value={paramValue}
                                            onChange={(e) => setParamValue(e.target.value)}
                                            placeholder="Enter Video ID or Email"
                                        />
                                    </div>
                                    <div className="additional-details-toggle" onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}>
                                        <span>Additional Details (optional)</span>
                                        <span className={`toggle-icon ${showAdditionalDetails ? 'open' : ''}`}>▼</span>
                                    </div>
                                    {showAdditionalDetails && (
                                        <>
                                            <div className="link-form-field">
                                                <label htmlFor="customUrl">
                                                    Enter post/page URL (optional):
                                                    <div className="tooltip-icon">
                                                        ?
                                                        <div className="tooltip-text">
                                                            Enter the URL of the source you want to track clicks for. This could be the Instagram/Linkedin/X etc post where this link will go. This will be stored and linked to your tracking data.
                                                        </div>
                                                    </div>
                                                </label>
                                                <input
                                                    type="url"
                                                    id="customUrl"
                                                    value={customUrl}
                                                    onChange={(e) => setCustomUrl(e.target.value)}
                                                    placeholder="https://example.com/video"
                                                />
                                            </div>
                                            <div className="link-form-field">
                                                <label htmlFor="customTags">Custom Tags (optional):</label>
                                                <TagInput
                                                    value={customTags}
                                                    onChange={setCustomTags}
                                                    placeholder="tag1, tag2, tag3"
                                                />
                                            </div>
                                            <div className="link-form-field">
                                                <label htmlFor="publishedDate">Published Date (optional):</label>
                                                <input
                                                    type="date"
                                                    id="publishedDate"
                                                    value={publishedDate}
                                                    onChange={(e) => setPublishedDate(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            <button type="submit" id="generateLink">Generate UTM Link</button>
                        </form>

                        <div id="result">
                            <h3>Generated Link:</h3>
                            <p id="generatedLink">{generatedLink || 'Your link will appear here...'}</p>
                            {generatedLink &&
                             generatedLink !== 'Please enter a valid landing page!' &&
                             generatedLink !== 'Please enter a valid YouTube video URL!' &&
                             generatedLink !== 'Please enter a value for the parameter!' && (
                                <>
                                    <button onClick={handleCopyLink}>Copy Link</button>
                                    <div className="link-placement-hint">
                                        <span className="link-placement-hint-icon">📌</span>
                                        <span className="link-placement-hint-text">{getLinkPlacementHint()}</span>
                                    </div>
                                </>
                            )}
                            {copySuccess && <p>{copySuccess}</p>}
                        </div>
                    </div>
                )}

                {/* ── QR Code Generator ── */}
                {activeTab === 'qr' && (
                    <QrCodeGenerator
                        username={username}
                        landingPages={landingPages}
                        onSaveLandingPage={saveLandingPage}
                        onRemoveLandingPage={removeLandingPage}
                    />
                )}

                {/* ── Automatic Link Updating ── */}
                {activeTab === 'auto' && (
                    youtubeName ? (
                        <div>
                            <h3 className="section-headline">Automatic Link Updating</h3>
                            <p className="auto-tab-desc">
                                Using this tool, you can let RevTrack automatically add the required tracking parameters to your existing links in your YouTube videos.
                            </p>
                            <AddVideoTracking noCollapse />
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-state-content">
                                <div className="empty-state-icon">🎥</div>
                                <h3 className="empty-state-title">
                                    Connect your YouTube channel to automatically add tracking links to all your videos with one click
                                </h3>
                                <button
                                    onClick={() => handleLoginWithYouTube(username)}
                                    className="empty-state-button"
                                >
                                    Login with YouTube →
                                </button>
                            </div>
                        </div>
                    )
                )}

                {/* ── Other Tools ── */}
                {activeTab === 'tools' && (
                    <div className="other-tools-panel">

                        {youtubeName ? (
                            <>
                                {/* Replace Links */}
                                <div className="other-tool-card update-tracking-links">
                                    <h3 className="section-headline">Replace Links in Video Descriptions</h3>
                                    <p className="other-tool-desc">
                                        Replace all instances of an old link with a new link across your video descriptions.
                                        Enter the plain links <strong>without</strong> any RevTrack tracking parameters.
                                    </p>
                                    <div className="other-tool-inputs">
                                        <input
                                            type="text"
                                            placeholder="Enter old link"
                                            value={oldLink}
                                            onChange={(e) => setOldLink(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Enter new link"
                                            value={newLink}
                                            onChange={(e) => setNewLink(e.target.value)}
                                        />
                                    </div>
                                    <button onClick={replaceLinksInVideos} disabled={isReplacing}>
                                        {isReplacing ? 'Replacing...' : 'Replace Links'}
                                    </button>
                                </div>

                                {/* Reset single video */}
                                <div className="other-tool-card clean-link-in-video">
                                    <h3 className="section-headline">Reset Link in a Video</h3>
                                    <p className="other-tool-desc">
                                        Remove all RevTrack tracking parameters from a specific link in a single video description.
                                    </p>
                                    <div className="other-tool-inputs">
                                        <input
                                            type="text"
                                            placeholder="Enter YouTube video link"
                                            value={resetVideoLink}
                                            onChange={(e) => setResetVideoLink(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Enter target URL"
                                            value={targetUrl}
                                            onChange={(e) => setTargetUrl(e.target.value)}
                                        />
                                    </div>
                                    <button onClick={cleanLinkInVideo}>Clean Link</button>
                                </div>

                                {/* Reset all videos */}
                                <div className="other-tool-card clean-link-in-all-videos">
                                    <h3 className="section-headline">Reset Link in All Videos</h3>
                                    <p className="other-tool-desc">
                                        Remove RevTrack tracking parameters from a specific link across <strong>all</strong> your video descriptions at once.
                                    </p>
                                    <div className="other-tool-inputs">
                                        <input
                                            type="text"
                                            placeholder="Enter target URL"
                                            value={targetUrlForAll}
                                            onChange={(e) => setTargetUrlForAll(e.target.value)}
                                        />
                                    </div>
                                    <button onClick={cleanLinkInAllVideos} disabled={isReplacing}>
                                        {isReplacing ? 'Cleaning...' : 'Clean Links'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-state-content">
                                    <div className="empty-state-icon">🔗</div>
                                    <h3 className="empty-state-title">
                                        Connect your YouTube channel to access link maintenance tools
                                    </h3>
                                    <button
                                        onClick={() => handleLoginWithYouTube(username)}
                                        className="empty-state-button"
                                    >
                                        Login with YouTube →
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Links;
